import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const { site } = useSite();
  const [open, setOpen] = useState(false);

  const links = [
    { id: "services", label: t("nav.services") },
    { id: "gallery", label: t("nav.gallery") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  const phone = site?.phone_primary || "";

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 w-full bg-black/85 backdrop-blur-md border-b border-zinc-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        {/* Logo */}
        <a href="#top" className="flex items-center gap-3" data-testid="navbar-logo">
          {site?.logo_url ? (
            <img
              src={site.logo_url}
              alt={site.business_name}
              className="h-12 w-12 sm:h-14 sm:w-14 object-contain"
            />
          ) : (
            <div className="h-12 w-12 sm:h-14 sm:w-14 bg-black border-2 border-brand-red flex items-center justify-center text-white font-display font-black text-base sm:text-lg">
              GBS
            </div>
          )}
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-black text-sm chrome-text tracking-tight">
              George Body Shop
            </span>
            <span className="tech-label !text-[10px] !tracking-[0.18em] !text-brand-red">
              COLLISION CENTER · NEWARK NJ
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`navlink-${l.id}`}
              className="text-sm font-medium text-zinc-300 hover:text-brand-red transition-colors duration-150"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:flex items-center border border-zinc-700 p-0.5" data-testid="lang-toggle">
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 text-xs font-bold tracking-wider transition-colors ${lang === "en" ? "bg-brand-red text-white" : "text-zinc-400 hover:text-white"}`}
            >EN</button>
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2.5 py-1 text-xs font-bold tracking-wider transition-colors ${lang === "es" ? "bg-brand-red text-white" : "text-zinc-400 hover:text-white"}`}
            >ES</button>
          </div>

          {phone && (
            <a
              data-testid="nav-call-btn"
              href={`tel:${phone}`}
              className="hidden md:inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redHover text-white px-4 py-2.5 font-bold text-sm transition-colors duration-150"
            >
              <Phone size={16} />
              {t("nav.callNow")}
            </a>
          )}

          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="lg:hidden border-t border-zinc-800 bg-black" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-zinc-200 py-2 border-b border-zinc-800"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center border border-zinc-700 p-0.5">
                <button onClick={() => setLang("en")} className={`px-2.5 py-1 text-xs font-bold ${lang === "en" ? "bg-brand-red text-white" : "text-zinc-300"}`}>EN</button>
                <button onClick={() => setLang("es")} className={`px-2.5 py-1 text-xs font-bold ${lang === "es" ? "bg-brand-red text-white" : "text-zinc-300"}`}>ES</button>
              </div>
              {phone && (
                <a href={`tel:${phone}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-red text-white px-4 py-2.5 font-bold text-sm">
                  <Phone size={16} /> {t("nav.callNow")}
                </a>
              )}
            </div>
            <Link to="/admin" className="tech-label text-center pt-2">{t("nav.admin")} →</Link>
          </div>
        </div>
      )}
    </header>
  );
}
